import { useRef } from 'react';
import './style.scss';
import EpiserverHelper from '../../../../helpers/episerver.helper';
import { IdHelper } from '../../../../helpers/id.helper';
import { TrafficSituationHelper } from '../../../../helpers/traffic-situation.helper';
import { ITrafficSituation } from '../../../../models/traffic-situation.model';
import { AnalyticsService } from '../../../../services/analytics-service';
import { TrafficInformationFeedbackService } from '../../../../services/traffic-information-feedback.service';
import { TranslationService } from '../../../../services/translation.service';
import AlternativesFact from '../facts/AlternativesFact';
import LineListFact from '../facts/LineListFact';
import ReasonFact from '../facts/ReasonFact';
import SeverityAndTitleFact from '../facts/SeverityAndTitleFact';
import ValidityPeriodFact from '../facts/ValidityPeriodFact';
import TrafficSituationTag from '../Tag';
import { TrafficSituationTagColor } from '../Tag/tag-color.enum';
import { ConfigurationService } from '../../../../services/configuration.service';

function AiEnhancedTrafficSituation({ trafficSituation, evaluationMode = false }: { trafficSituation: ITrafficSituation; evaluationMode?: boolean }) {
  // === REFS ===
  const collapseButtonRef = useRef<HTMLAnchorElement>(null);

  // RENDER GUARD
  if (!trafficSituation || !trafficSituation.aiGeneratedInformation) {
    return null;
  }

  // VARIABLES
  const isNew = TrafficSituationHelper.isNewTrafficSituation(trafficSituation);
  const id = IdHelper.newUniqueId('traffic-situation-collapsible-');
  const useAiEnhancedInformation = ConfigurationService.get('trafficsituationspage.useaienhancedinformation') === 'True';
  const showOnlyAiEnhancedInformation = useAiEnhancedInformation && ConfigurationService.get('trafficsituationspage.showonlyaienhancedinformation') === 'True';

  // === EVENT HANDLERS ===
  function handleShowMoreDetailsClick() {
    AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Klick på visa mer information', '');
  }

  function handleExpandTrafficSituationDetails(trafficSituation: ITrafficSituation) {
    AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Visa störningsmeddelande-detaljer', '');
    TrafficInformationFeedbackService.registerExplicitFeedback(trafficSituation);
    if (collapseButtonRef.current?.classList.contains('collapsed')) {
      collapseButtonRef.current.setAttribute('aria-label', TranslationService.get('common.expand'));
    } else {
      collapseButtonRef.current?.setAttribute('aria-label', TranslationService.get('common.collapse'));
    }
  }

  // RENDER
  // Used by the TrafficSituationAiEnhancementEvaluation component
  if (evaluationMode)
    return (
      <div className="ai-enhanced-traffic-situation bg-white border border-gray-700 rounded p-3 last-child-margin-bottom-none">
        <SeverityAndTitleFact trafficSituation={trafficSituation} className="mb-2" />
        <LineListFact trafficSituation={trafficSituation} className="mb-2" />
        <ValidityPeriodFact trafficSituation={trafficSituation} showOnlyAiEnhancedInformation={evaluationMode} className="mb-2" />
        <AlternativesFact trafficSituation={trafficSituation} className="mb-2" />
        <ReasonFact trafficSituation={trafficSituation} className="mb-2" />
      </div>
    );

  return (
    <div className="ai-enhanced-traffic-situation bg-white border border-gray-700 rounded p-3 print-no-break position-relative">
      {EpiserverHelper.isInEditMode() && <span className="fw-bold">{trafficSituation.situationNumber}</span>}
      <div className="ai-enhanced-traffic-situation__tags d-flex gap-1">
        {isNew && <TrafficSituationTag text={TranslationService.get('trafficsituationspage.tag.new')} />}
        {!showOnlyAiEnhancedInformation && <TrafficSituationTag text="AI" color={TrafficSituationTagColor.Labs} />}
      </div>
      <a
        ref={collapseButtonRef}
        className="d-flex ai-enhanced-traffic-situation__collapse-button collapsed text-decoration-none"
        data-bs-toggle="collapse"
        href={`#${id}`}
        role="button"
        aria-expanded="false"
        aria-controls={id}
        aria-label={TranslationService.get('common.expand')}
        onClick={() => handleExpandTrafficSituationDetails(trafficSituation)}
      >
        <div className="d-flex flex-column flex-grow-1 last-child-margin-bottom-none">
          <SeverityAndTitleFact trafficSituation={trafficSituation} className="mb-2" />
          <LineListFact trafficSituation={trafficSituation} className="mb-2" />
          <ValidityPeriodFact trafficSituation={trafficSituation} className="mb-2" />
        </div>
      </a>
      <div className="collapse last-child-margin-bottom-none" id={id}>
        <div className={trafficSituation.aiGeneratedInformation?.alternatives || trafficSituation.aiGeneratedInformation?.reason || trafficSituation.relativeDetailsUrl ? 'pt-2' : undefined}>
          <AlternativesFact trafficSituation={trafficSituation} className="mb-2" />
          <ReasonFact trafficSituation={trafficSituation} className="mb-2" />
          {trafficSituation.relativeDetailsUrl && (
            <div className="mb-2">
              <a href={trafficSituation.relativeDetailsUrl} onClick={handleShowMoreDetailsClick}>
                {TranslationService.get('trafficsituationspage.linktodetailspagetext')}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AiEnhancedTrafficSituation;
